import { useEffect, useState, useRef } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { BlockRenderer } from "@/blocks";
import { MessageCircle, Send, X, Phone, Mail } from "lucide-react";
import PoweredByNariai from "./PoweredByNariai";

// ---- Bilingual nav / footer label lookup ----
// Applied client-side on Spanish pages so we don't need to duplicate site.nav
// or site.footer in the DB. Keys are the exact English labels stored in
// `sites.nav[*].label` and `sites.footer.columns[*].heading` / `*.links[*].label`.
const ES_LABEL_LOOKUP = {
  // Nav
  "Services": "Servicios",
  "Industries": "Industrias",
  "Projects": "Proyectos",
  "Learn": "Aprender",
  "Tools": "Herramientas",
  "Locations": "Ubicaciones",
  "Map": "Mapa",
  "Toolkit": "Recursos",
  "Awards": "Reconocimientos",
  "Testimonials": "Testimonios",
  "About": "Nosotros",
  "Contact": "Contacto",
  // Footer columns
  "Company": "Empresa",
  // Footer links
  "Line Striping": "Demarcación de líneas",
  "Sealcoating": "Sellado",
  "ADA Compliance": "Cumplimiento ADA",
  "Fire Lane Marking": "Carriles de emergencia",
  "Stencils & Wayfinding": "Plantillas y señalización",
  "Coverage Areas": "Áreas de cobertura",
  "FAQ": "Preguntas frecuentes",
  "Glossary": "Glosario",
  "Privacy Policy": "Política de privacidad",
  "Terms of Service": "Términos del servicio",
  "Dallas Resource Center": "Centro de recursos Dallas",
  "Garland (HQ)": "Garland (Oficina central)",
  "All DFW Cities": "Todas las ciudades del DFW",
  "Rockwall": "Rockwall",
};
const t = (label, isSpanish) => (isSpanish ? (ES_LABEL_LOOKUP[label] || label) : label);

/**
 * Generic tenant public site — reads `sites` + `pages` from Nariai's Website Engine
 * and renders any tenant's pages using the block library.
 *
 * If `slugOverride` is passed (from domain routing), skips useParams.
 */
export default function TenantSite({ slugOverride }) {
  const params = useParams();
  const slug = slugOverride || params.slug || null;
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/public/site/${slug}`)
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  // Determine sub-path relative to the site root. Domain-routed sites live at root,
  // while /site/:slug sites strip that prefix first.
  const rawPath = location.pathname;
  const subPath = slugOverride
    ? (rawPath || "/")
    : (rawPath.replace(new RegExp(`^/site/${slug}`), "") || "/");

  const page = data?.pages?.find((p) => p.slug === subPath) || data?.pages?.find((p) => p.slug === "/");
  const notFoundPage = data?.pages?.find((p) => p.slug === "/404");
  const isMissing = data && subPath !== "/" && !data.pages?.find((p) => p.slug === subPath);
  const activePage = isMissing ? notFoundPage : page;

  // ---- Spanish / bilingual support ----
  // Spanish pages have slugs prefixed with "/es" (e.g. "/es", "/es/services").
  const isSpanish = subPath === "/es" || subPath.startsWith("/es/");
  const enEquivalent = isSpanish ? (subPath === "/es" ? "/" : subPath.slice(3)) : subPath;
  const esEquivalent = isSpanish ? subPath : (subPath === "/" ? "/es" : `/es${subPath}`);
  // Set of all published slugs for quick alt-language existence checks.
  const publishedSlugs = data?.pages?.map((p) => p.slug) || [];
  const hasSpanishAlt = publishedSlugs.includes(esEquivalent);
  const hasEnglishAlt = publishedSlugs.includes(enEquivalent);

  // If a user lands on an /es/* URL that has no Spanish page (e.g. deep
  // silo article we haven't translated), redirect them to the English
  // version instead of showing a 404. English fallback is better UX and
  // avoids fake 404 hreflang entries in Google's index.
  useEffect(() => {
    if (!data) return;
    if (!isSpanish) return;
    if (publishedSlugs.includes(subPath)) return;
    // Spanish URL has no page — but the English equivalent does. Redirect.
    if (publishedSlugs.includes(enEquivalent)) {
      const target = slugOverride ? enEquivalent : `/site/${slug}${enEquivalent}`;
      navigate(target, { replace: true });
    }
  }, [data, isSpanish, subPath, enEquivalent, slug, slugOverride, navigate, publishedSlugs]);

  // Smart soft-404 recovery: if the user (or Googlebot) hits a bare slug
  // like /parking-lot-striping that's really /services/parking-lot-striping,
  // find the matching real slug and redirect there. This eliminates soft
  // 404s for any old sitemap URLs, external backlinks, or Googlebot URL
  // pattern guesses.
  useEffect(() => {
    if (!data) return;
    if (subPath === "/") return;
    if (publishedSlugs.includes(subPath)) return; // page exists — nothing to do
    if (isSpanish) return; // handled by the Spanish-fallback effect above

    // Short-form / historical aliases: common ways users and inbound links
    // refer to services in shorthand. These map to their real /services/…
    // slug so /ada-compliance, /handicap, /fire-lane, etc. never soft-404.
    const SHORT_FORM_ALIASES = {
      "/ada": "/services/ada-compliance-striping",
      "/ada-compliance": "/services/ada-compliance-striping",
      "/ada-parking": "/services/ada-parking-stall-installation",
      "/handicap": "/services/ada-compliance-striping",
      "/handicap-striping": "/services/ada-compliance-striping",
      "/fire-lane": "/services/fire-lane-striping",
      "/fire-lanes": "/services/fire-lane-striping",
      "/striping": "/services/parking-lot-striping",
      "/re-striping": "/services/parking-lot-restriping",
      "/restriping": "/services/parking-lot-restriping",
      "/warehouse": "/services/warehouse-floor-striping",
      "/warehouse-striping": "/services/warehouse-floor-striping",
      "/pavement-marking": "/services/pavement-markings",
      "/signage": "/services/parking-lot-signage",
      "/parking-signage": "/services/parking-lot-signage",
      "/curb": "/services/curb-painting",
      "/bollard": "/services/bollard-painting",
      "/bollards": "/services/bollard-painting",
      "/wheel-stops": "/services/wheel-stop-installation",
      "/wheel-stop": "/services/wheel-stop-installation",
      "/sandblast": "/services/sandblasting",
      "/crosswalk": "/services/crosswalk-striping",
      "/crosswalks": "/services/crosswalk-striping",
      "/arrows": "/services/directional-arrows",
      "/loading-zone": "/services/loading-zone-striping",
      "/no-parking": "/services/no-parking-zones",
      "/speed-bumps": "/services/speed-bump-markings",
      "/speed-bump": "/services/speed-bump-markings",
      "/layout": "/services/parking-lot-layout-design",
      "/layout-design": "/services/parking-lot-layout-design",
      "/line-removal": "/services/line-removal",
      "/services-list": "/services",
      "/service": "/services",
    };
    const shortAlias = SHORT_FORM_ALIASES[subPath.replace(/\/+$/, "").toLowerCase()];
    if (shortAlias && publishedSlugs.includes(shortAlias)) {
      const target = slugOverride ? shortAlias : `/site/${slug}${shortAlias}`;
      navigate(target, { replace: true });
      return;
    }

    // Try common section prefixes for bare-slug URLs (order = priority).
    const CANDIDATE_PREFIXES = ["/services", "/industries", "/locations", "/projects", "/learn", "/tools"];
    for (const prefix of CANDIDATE_PREFIXES) {
      const candidate = `${prefix}${subPath}`;
      if (publishedSlugs.includes(candidate)) {
        const target = slugOverride ? candidate : `/site/${slug}${candidate}`;
        navigate(target, { replace: true });
        return;
      }
    }
    // Also strip a trailing slash, if any (Google sometimes indexes both).
    if (subPath.endsWith("/") && subPath.length > 1) {
      const noSlash = subPath.replace(/\/+$/, "");
      if (publishedSlugs.includes(noSlash)) {
        const target = slugOverride ? noSlash : `/site/${slug}${noSlash}`;
        navigate(target, { replace: true });
      }
    }
  }, [data, subPath, isSpanish, slug, slugOverride, navigate, publishedSlugs]);

  // SEO — inject title, meta description, canonical, OG, Twitter, JSON-LD schemas
  useEffect(() => {
    if (!data || !activePage) return;
    const { tenant, site } = data;
    const seo = activePage.seo || {};
    const title = seo.title || `${activePage.title} — ${tenant.name}`;
    const desc = seo.description || tenant.description || "";
    const origin = process.env.REACT_APP_SITE_URL || window.location.origin;

    // Canonical URL — always points to the tenant's production domain (never
    // to the preview host, so Google indexes lonestarstriping.pro and not
    // the *.preview.emergentagent.com twin). Falls back to the current origin
    // if the tenant has no production domain set yet.
    const canonicalOrigin = tenant.domain ? `https://${tenant.domain}` : origin;
    // Strip the /site/{slug} preview prefix from the pathname so canonicals
    // always express the customer-visible URL.
    const rawPath = window.location.pathname || "/";
    const cleanPath = rawPath.replace(new RegExp(`^/site/${tenant.slug}`), "") || "/";
    const canonicalUrl = seo.canonical || `${canonicalOrigin}${cleanPath}`;
    const ogImage = seo.og_image || tenant.logo_url || "";

    document.title = title;
    upsertMeta("name", "description", desc);
    upsertLink("canonical", canonicalUrl);
    // Robots — send noindex,nofollow for the built-in 404 fallback and for
    // any page where SEO explicitly opts out. This is what tells Google to
    // drop soft-404 URLs (e.g. old /parking-lot-striping links) from its index.
    const isSoft404 = isMissing || activePage.slug === "/404" || seo.noindex === true;
    upsertMeta("name", "robots", isSoft404 ? "noindex,nofollow" : "index,follow,max-image-preview:large");
    // Open Graph
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", tenant.name);
    if (ogImage) upsertMeta("property", "og:image", ogImage);
    // Twitter card
    upsertMeta("name", "twitter:card", ogImage ? "summary_large_image" : "summary");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", desc);
    if (ogImage) upsertMeta("name", "twitter:image", ogImage);

    // Nariai attribution — meta tags on every public tenant page
    upsertMeta("name", "author", "Nariai AI");
    upsertMeta("name", "generator", "Nariai Website Engine");

    // JSON-LD structured data
    const ldScripts = [];
    ldScripts.push(buildOrganizationSchema(tenant, origin));
    // Sitewide WebSite schema with Nariai as creator — merged, not duplicated
    ldScripts.push(buildWebSiteSchema(tenant, origin));
    if (activePage.slug === "/" || seo.schema_type === "LocalBusiness") {
      ldScripts.push(buildLocalBusinessSchema(tenant, origin, site));
    }
    const faqBlock = (activePage.blocks || []).find((b) => b.type === "faq" && b.visible !== false);
    if (faqBlock) ldScripts.push(buildFaqSchema(faqBlock.props?.items || []));
    // Reviews / testimonials Schema.org markup — emits AggregateRating + Review array
    const reviewBlocks = (activePage.blocks || []).filter(
      (b) => (b.type === "reviews" || b.type === "testimonials_carousel") && b.visible !== false
    );
    if (reviewBlocks.length) {
      const allItems = reviewBlocks.flatMap((b) => b.props?.items || []);
      const reviewSchema = buildReviewsSchema(tenant, origin, allItems);
      if (reviewSchema) ldScripts.push(reviewSchema);
    }
    ldScripts.push(buildBreadcrumbSchema(tenant, activePage, origin, slug, !!slugOverride));

    upsertJsonLd(ldScripts.filter(Boolean));

    // ---- hreflang alternates + html lang ----
    // Emit English + Spanish alternates so Google serves the right locale.
    // Only emit each language when that variant actually exists (or is the
    // current page). This prevents fake 404 URLs from being indexed.
    const linkBaseForHreflang = slugOverride ? "" : `/site/${slug}`;
    const enHref = `${canonicalOrigin}${linkBaseForHreflang}${enEquivalent === "/" ? "" : enEquivalent}` || canonicalOrigin;
    const esHref = `${canonicalOrigin}${linkBaseForHreflang}${esEquivalent}`;
    const alternates = [];
    const includeEn = !isSpanish || hasEnglishAlt;
    const includeEs = isSpanish ? publishedSlugs.includes(subPath) : hasSpanishAlt;
    if (includeEn) {
      alternates.push({ hreflang: "en", href: enHref });
      alternates.push({ hreflang: "en-us", href: enHref });
      alternates.push({ hreflang: "x-default", href: enHref });
    }
    if (includeEs) {
      alternates.push({ hreflang: "es", href: esHref });
      alternates.push({ hreflang: "es-us", href: esHref });
      alternates.push({ hreflang: "es-mx", href: esHref });
    }
    upsertAlternates(alternates);
    try { document.documentElement.setAttribute("lang", isSpanish ? "es" : "en"); } catch { /* noop */ }

    return () => {
      // no-op cleanup — leave tags in place for the next page render
    };
  }, [data, activePage, slug, slugOverride, isSpanish, enEquivalent, esEquivalent, hasSpanishAlt, hasEnglishAlt, isMissing, subPath, publishedSlugs]);

  // Set body background to match tenant theme to prevent Nariai off-white flash on scroll
  const themeName = data?.site?.settings?.theme || "light";
  useEffect(() => {
    if (themeName === "dark-commercial") {
      const prev = document.body.style.background;
      document.body.style.background = "#0D0D0D";
      return () => { document.body.style.background = prev; };
    }
  }, [themeName]);

  if (notFound) return <div className="min-h-screen flex items-center justify-center tenant-site-theme">Brand not found.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center tenant-site-theme text-[var(--l-text-sec)]">Loading…</div>;
  if (!activePage) return null;

  // /powered-by-nariai is a standalone NariAI landing page hosted on the
  // client's domain. Render it without any tenant chrome (client nav,
  // footer, ticker, chat widget). Placed here — after every hook has run —
  // so we obey React's Rules of Hooks.
  if (subPath === "/powered-by-nariai" || subPath === "/powered-by-nariai/") {
    return <PoweredByNariai />;
  }

  const { tenant, site } = data;
  const linkBase = slugOverride ? "" : `/site/${slug}`;
  const themeClass = themeName === "dark-commercial" ? "tenant-theme-dark" : "";

  // Delegated click handler — rewrites all bare-path anchors (`/services/...`,
  // `/projects/...`, `/learn/...`, etc.) to the tenant-namespaced route so
  // clicks from Related Resources, ProjectsGrid, ArticleBlock etc. keep users
  // inside the tenant sandbox (fixes iteration_11 critical link-escape bug).
  const handleTenantClick = (e) => {
    const anchor = e.target.closest && e.target.closest("a[href]");
    if (!anchor) return;
    const href = anchor.getAttribute("href");
    if (!href) return;
    // ignore: external, anchors, mailto, tel, absolute urls, already-tenant-prefixed, and Link-managed
    if (
      href.startsWith("http") ||
      href.startsWith("//") ||
      href.startsWith("#") ||
      href.startsWith("mailto:") ||
      href.startsWith("tel:") ||
      href.startsWith("/site/") ||
      anchor.target === "_blank" ||
      anchor.dataset.tenantLink === "raw"
    ) return;
    if (!href.startsWith("/")) return; // relative or protocol-relative — leave alone
    // While on a Spanish page, keep internal links inside the /es namespace when
    // a Spanish version of the target exists. Otherwise fall back to English.
    let effectiveHref = href;
    if (isSpanish && !href.startsWith("/es")) {
      const candidateEs = href === "/" ? "/es" : `/es${href}`;
      if (publishedSlugs.includes(candidateEs)) {
        effectiveHref = candidateEs;
      }
    }
    e.preventDefault();
    const target = slugOverride ? effectiveHref : `/site/${slug}${effectiveHref}`;
    navigate(target);
  };

  return (
    <div
      className={`tenant-site-theme ${themeClass} min-h-screen`}
      data-testid="tenant-site"
      data-slug={slug}
      data-theme={themeName}
      onClick={handleTenantClick}
    >
      <Nav
        tenant={tenant}
        site={site}
        linkBase={linkBase}
        theme={themeName}
        isHome={activePage?.slug === "/" || activePage?.slug === "/es"}
        isSpanish={isSpanish}
        enEquivalent={enEquivalent}
        esEquivalent={esEquivalent}
        hasSpanishAlt={hasSpanishAlt}
        hasEnglishAlt={hasEnglishAlt}
      />
      {activePage.blocks.map((b, i) => (
        <BlockRenderer key={b.id || `blk-${i}`} block={b} tenant={tenant} site={site} page={activePage} />
      ))}
      <Footer
        tenant={tenant}
        site={site}
        linkBase={linkBase}
        theme={themeName}
        isSpanish={isSpanish}
        enEquivalent={enEquivalent}
        esEquivalent={esEquivalent}
        hasSpanishAlt={hasSpanishAlt}
        hasEnglishAlt={hasEnglishAlt}
      />
      <ChatWidget tenant={tenant} />
    </div>
  );
}

// ---------- SEO helpers ----------
function upsertMeta(attr, name, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// Rebuild the hreflang <link rel="alternate"> set. Removes any previously
// injected alternates so language switches don't leak stale entries.
function upsertAlternates(alternates) {
  document.querySelectorAll('link[rel="alternate"][data-nariai-alt="true"]').forEach((n) => n.remove());
  alternates.forEach(({ hreflang, href }) => {
    const el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", hreflang);
    el.setAttribute("href", href);
    el.setAttribute("data-nariai-alt", "true");
    document.head.appendChild(el);
  });
}

function upsertJsonLd(schemas) {
  // Remove any previously injected Nariai JSON-LD
  document.querySelectorAll('script[data-nariai-ld="true"]').forEach((n) => n.remove());
  for (const schema of schemas) {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-nariai-ld", "true");
    s.text = JSON.stringify(schema);
    document.head.appendChild(s);
  }
}

function buildOrganizationSchema(tenant, origin) {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: tenant.name,
    url: tenant.domain ? `https://${tenant.domain}` : origin,
    logo: tenant.logo_url || undefined,
    description: tenant.description || tenant.tagline || undefined,
    email: tenant.email || undefined,
    telephone: tenant.phone || undefined,
  };
}

// Sitewide WebSite schema — identifies Nariai AI as the website creator /
// technology provider (not owner, not service provider). Merged into the JSON-LD
// stream so it never duplicates Organization or LocalBusiness.
function buildWebSiteSchema(tenant, origin) {
  const siteUrl = tenant.domain ? `https://${tenant.domain}` : origin;
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: tenant.name,
    url: siteUrl,
    inLanguage: "en-US",
    creator: {
      "@type": "Organization",
      name: "Nariai AI",
      url: "https://nariai.ai",
    },
  };
}

function buildLocalBusinessSchema(tenant, origin, site) {
  const hours = (site?.settings?.business_hours || []).map((h) => `${h.day} ${h.hours}`);
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: tenant.name,
    image: tenant.logo_url || undefined,
    "@id": tenant.domain ? `https://${tenant.domain}/#business` : `${origin}/#business`,
    url: tenant.domain ? `https://${tenant.domain}` : origin,
    telephone: tenant.phone || undefined,
    email: tenant.email || undefined,
    address: tenant.address ? {
      "@type": "PostalAddress",
      streetAddress: tenant.address,
    } : undefined,
    description: tenant.description || undefined,
    priceRange: "$$",
    openingHours: hours.length ? hours : undefined,
  };
}

function buildFaqSchema(items) {
  if (!items?.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}

function buildReviewsSchema(tenant, origin, items) {
  const clean = (items || []).filter((it) => it && it.body && it.author);
  if (!clean.length) return null;
  const ratings = clean.map((it) => parseInt(it.stars, 10) || 5);
  const avg = (ratings.reduce((s, n) => s + n, 0) / ratings.length).toFixed(1);
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${origin}#business`,
    name: tenant.name,
    url: origin,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: avg,
      reviewCount: clean.length,
      bestRating: "5",
      worstRating: "1",
    },
    review: clean.map((it) => ({
      "@type": "Review",
      reviewRating: {
        "@type": "Rating",
        ratingValue: String(parseInt(it.stars, 10) || 5),
        bestRating: "5",
        worstRating: "1",
      },
      author: {
        "@type": "Person",
        name: it.author,
      },
      reviewBody: it.body,
      ...(it.city ? { locationCreated: { "@type": "Place", name: it.city } } : {}),
    })),
  };
}

function buildBreadcrumbSchema(tenant, page, origin, slug, isDomainRouted) {
  const rootUrl = isDomainRouted ? origin : `${origin}/site/${slug}`;
  const items = [
    { "@type": "ListItem", position: 1, name: "Home", item: rootUrl },
  ];
  if (page.slug !== "/") {
    items.push({
      "@type": "ListItem",
      position: 2,
      name: page.title,
      item: `${rootUrl}${page.slug}`,
    });
  }
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

// ---------- Nav ----------
function Nav({ tenant, site, linkBase, theme, isHome = false, isSpanish = false, enEquivalent = "/", esEquivalent = "/es", hasSpanishAlt = false, hasEnglishAlt = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);
  // Close mobile menu on route change
  const location = useLocation();
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  // Lock body scroll when mobile menu open
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  const isDark = theme === "dark-commercial";
  const phone = site?.settings?.phones?.[0] || tenant.phone;

  if (isDark) {
    const tickerItems = isSpanish ? [
      { icon: "trophy", text: "LSS es el EMPLEADOR DEL AÑO — 2026", accent: true },
      { icon: "trophy", text: "3× Empleador del Año · 2024 · 2025 · 2026" },
      { icon: "trophy", text: "Ganador del premio de la Cámara de Comercio de Rockwall" },
      { icon: "star", text: "30% de descuento para veteranos", accent: true },
    ] : [
      { icon: "trophy", text: "LSS is THIS YEAR'S Employer of the Year — 2026", accent: true },
      { icon: "trophy", text: "3× Employer of the Year · 2024 · 2025 · 2026" },
      { icon: "trophy", text: "Rockwall Chamber of Commerce Award-Winner" },
      { icon: "star", text: "30% Off for Veterans", accent: true },
    ];
    const TrophyIcon = () => (
      <svg width="20" height="24" viewBox="0 0 24 28" fill="none" aria-hidden style={{ verticalAlign: "middle" }}>
        <defs>
          <linearGradient id="tg-star" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#F5E6B3" />
            <stop offset="45%" stopColor="#E5C77A" />
            <stop offset="100%" stopColor="#A1782E" />
          </linearGradient>
          <linearGradient id="tg-base" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3B2B14" />
            <stop offset="50%" stopColor="#1B120A" />
            <stop offset="100%" stopColor="#0A0603" />
          </linearGradient>
          <linearGradient id="tg-plate" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F0D580" />
            <stop offset="50%" stopColor="#C9A24B" />
            <stop offset="100%" stopColor="#8F6A2C" />
          </linearGradient>
        </defs>
        {/* faceted 5-point star */}
        <path
          d="M12 1.5 L14.7 8.5 L22.2 9.1 L16.5 14 L18.3 21.3 L12 17.4 L5.7 21.3 L7.5 14 L1.8 9.1 L9.3 8.5 Z"
          fill="url(#tg-star)"
          stroke="#7A5A20"
          strokeWidth="0.4"
          strokeLinejoin="round"
        />
        {/* subtle inner facet highlights */}
        <path d="M12 1.5 L12 17.4" stroke="#FFF6D8" strokeWidth="0.35" opacity="0.55" />
        <path d="M14.7 8.5 L9.3 8.5" stroke="#FFF6D8" strokeWidth="0.2" opacity="0.4" />
        {/* base pedestal */}
        <rect x="4.5" y="21.5" width="15" height="4.5" rx="0.6" fill="url(#tg-base)" />
        {/* gold plate accent */}
        <rect x="6" y="23" width="12" height="1.6" fill="url(#tg-plate)" />
      </svg>
    );
    const StarIcon = () => (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
      </svg>
    );
    const iconFor = (kind) => kind === "star" ? <StarIcon /> : <TrophyIcon />;
    const renderTickerBlock = (keyPrefix) => (
      <>
        {tickerItems.map((it, i) => (
          <span key={`${keyPrefix}-${i}`} style={{ display: "inline-flex", alignItems: "center", gap: "3rem" }}>
            <span className={`ticker-item${it.accent ? " accent" : ""}`}>
              {iconFor(it.icon)}
              <span>{it.text}</span>
            </span>
            <span className="ticker-sep">◆</span>
          </span>
        ))}
      </>
    );
    return (
      <>
        {/* Homepage-only: subtle marquee ticker (kept for regulars) */}
        {isHome && (
          <div className="ticker-strip" data-testid="ticker-strip">
            <div className="ticker-track">
              {renderTickerBlock("a")}
              {renderTickerBlock("b")}
            </div>
          </div>
        )}
        <nav
          className={`sticky top-0 z-40 transition-all duration-300 marble-surface`}
          data-testid="tenant-nav"
          data-is-home={isHome ? "1" : "0"}
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(20,22,26,0.72) 0%, rgba(20,22,26,0.55) 55%, rgba(20,22,26,0.78) 100%), url(/marble-nav-1600.jpg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            boxShadow: scrolled
              ? "0 14px 34px -18px rgba(0,0,0,0.7)"
              : "0 6px 18px -12px rgba(0,0,0,0.5)",
            backdropFilter: "blur(10px) saturate(140%)",
          }}
        >
          <div className={`max-w-[1760px] mx-auto px-4 md:px-6 lg:px-8 flex items-center justify-between gap-3 lg:gap-4 ${isHome ? "py-2 md:py-2.5" : "py-1.5 md:py-2"}`}>
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4 shrink-0 min-w-0">
              {/* Nav-bar logo intentionally hidden on the home page — the hero
                  displays a large centered logo, no need to duplicate it here.
                  On sub-pages we keep a small logo for anchor branding. */}
              {!isHome && (
                <Link
                  to={linkBase || "/"}
                  className="flex items-center gap-3 relative shrink-0"
                  data-testid="tenant-nav-logo"
                >
                  {tenant.logo_url ? (
                    <>
                      <span
                        aria-hidden
                        className="absolute inset-0 -z-10 rounded-full blur-2xl"
                        style={{ background: "radial-gradient(circle, rgba(229,199,122,0.32) 0%, rgba(201,162,75,0.14) 45%, transparent 70%)", transform: "scale(1.4)" }}
                      />
                      <img
                        src={tenant.logo_url}
                        alt={tenant.name}
                        className="h-10 sm:h-12 md:h-14 lg:h-16 xl:h-18 w-auto"
                        style={{
                          display: "block",
                          filter: "drop-shadow(0 6px 16px rgba(0,0,0,0.7))",
                        }}
                      />
                    </>
                  ) : (
                    <div className="w-14 h-14 rounded-full flex items-center justify-center text-white font-cab font-black text-2xl" style={{ background: tenant.primary_color }}>
                      {tenant.name[0]}
                    </div>
                  )}
                </Link>
              )}
            </div>
            <div className="hidden min-[1400px]:flex items-center gap-4 2xl:gap-6 font-inter text-[11px] 2xl:text-[12px] font-semibold tracking-[0.14em] 2xl:tracking-[0.18em] uppercase min-w-0 flex-nowrap" data-testid="tenant-nav-links">
              {(site?.nav || [])
                // Spanish labels are ~15% longer than English; trim 3 secondary
                // items (Map, Toolkit, Awards) so the language switcher + CTA
                // don't get overlapped on 1400–1600px viewports. All items
                // remain accessible via the footer / mobile menu.
                .filter((n) => !isSpanish || !["/service-area", "/toolkit", "/awards"].includes(n.href))
                .map((n) => {
                const target = `${linkBase}${n.href === "/" ? "" : n.href}` || "/";
                const isActive = location.pathname === target || (n.href !== "/" && location.pathname.startsWith(target));
                return (
                  <Link
                    key={n.href}
                    to={target}
                    className="nav-link-gold text-white/85 hover:text-white transition-colors"
                    aria-current={isActive ? "page" : undefined}
                    style={{ textShadow: "0 2px 8px rgba(0,0,0,0.75)" }}
                  >
                    {t(n.label, isSpanish)}
                  </Link>
                );
              })}
            </div>
            <div className="flex items-center gap-2 md:gap-3 shrink-0 min-w-0">
              {/* Language switcher — always visible next to the hamburger / CTA.
                  Compact so it never crowds the nav or the primary CTA. */}
              <LanguageSwitcher
                isSpanish={isSpanish}
                enEquivalent={enEquivalent}
                esEquivalent={esEquivalent}
                hasSpanishAlt={hasSpanishAlt}
                hasEnglishAlt={hasEnglishAlt}
                linkBase={linkBase}
                variant="nav-dark-compact"
              />
              <div className="hidden min-[1400px]:inline-flex">
                <Link
                  to={`${linkBase}${isSpanish ? "/es/contact" : "/contact"}`}
                  className="gold-cta text-xs md:text-sm whitespace-nowrap"
                  data-testid="nav-cta"
                >
                  {isSpanish ? "Cotización gratis" : "Free estimate"} <span aria-hidden style={{ fontSize: 12 }}>★</span>
                </Link>
              </div>
              {/* Mobile hamburger */}
              <button
                type="button"
                aria-label="Open menu"
                aria-expanded={mobileOpen}
                onClick={() => setMobileOpen((v) => !v)}
                data-testid="mobile-menu-toggle"
                className="min-[1400px]:hidden inline-flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-md text-white shrink-0"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(229,199,122,0.35)" }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  {mobileOpen ? (
                    <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>
                  ) : (
                    <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>
                  )}
                </svg>
              </button>
            </div>
          </div>
          {/* Gold hairline at the bottom of the nav */}
          <div className="gold-hairline" aria-hidden />
        </nav>
        {/* Mobile slide-in menu */}
        {mobileOpen && (
          <div
            className="min-[1400px]:hidden fixed inset-0 z-[80] tenant-theme-dark"
            data-testid="mobile-menu-panel"
            style={{ background: "rgba(10,10,10,0.96)", backdropFilter: "blur(12px)" }}
            onClick={(e) => { if (e.target === e.currentTarget) setMobileOpen(false); }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <Link to={linkBase || "/"} onClick={() => setMobileOpen(false)} className="flex items-center gap-3" data-testid="mobile-menu-logo">
                {tenant.logo_url && <img src={tenant.logo_url} alt={tenant.name} className="h-10 w-auto" />}
              </Link>
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setMobileOpen(false)}
                className="w-11 h-11 rounded-md text-white flex items-center justify-center"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}
                data-testid="mobile-menu-close"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <nav className="px-6 py-6 overflow-y-auto" style={{ maxHeight: "calc(100vh - 64px)" }}>
              <ul className="space-y-1 font-outfit">
                {(site?.nav || []).map((n) => (
                  <li key={n.href}>
                    <Link
                      to={`${linkBase}${n.href === "/" ? "" : n.href}` || "/"}
                      onClick={() => setMobileOpen(false)}
                      className="block px-3 py-3 rounded-md text-white/90 hover:text-white hover:bg-white/5 text-lg font-medium tracking-wide"
                      data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {t(n.label, isSpanish)}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <LanguageSwitcher
                  isSpanish={isSpanish}
                  enEquivalent={enEquivalent}
                  esEquivalent={esEquivalent}
                  hasSpanishAlt={hasSpanishAlt}
                  hasEnglishAlt={hasEnglishAlt}
                  linkBase={linkBase}
                  variant="mobile"
                  onNavigate={() => setMobileOpen(false)}
                />
              </div>
              <div className="mt-8 grid grid-cols-1 gap-3">
                {phone && (
                  <a
                    href={`tel:${phone.replace(/[^0-9+]/g, "")}`}
                    className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-md text-white font-semibold"
                    style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)" }}
                    data-testid="mobile-call-cta"
                  >
                    <Phone size={16} /> {isSpanish ? `Llamar ${phone}` : `Call ${phone}`}
                  </a>
                )}
                <Link
                  to={`${linkBase}${isSpanish ? "/es/contact" : "/contact"}`}
                  onClick={() => setMobileOpen(false)}
                  className="w-full inline-flex items-center justify-center py-3 rounded-md font-semibold text-black"
                  style={{ background: tenant.primary_color }}
                  data-testid="mobile-estimate-cta"
                >
                  {isSpanish ? "Cotización gratis" : "Free estimate"}
                </Link>
              </div>
            </nav>
          </div>
        )}
        {/* Sticky mobile bottom action bar (hidden while menu open) */}
        {!mobileOpen && (
          <div
            className="xl:hidden fixed bottom-0 left-0 right-0 z-40 grid grid-cols-2 gap-2 p-3"
            data-testid="mobile-sticky-bar"
            style={{ background: "linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.85))" }}
          >
            {phone && (
              <a
                href={`tel:${phone.replace(/[^0-9+]/g, "")}`}
                className="flex items-center justify-center gap-2 py-3 rounded-md text-white font-semibold text-sm"
                style={{ background: "rgba(20,20,20,0.92)", border: "1px solid rgba(255,255,255,0.14)" }}
                data-testid="mobile-sticky-call"
              >
                <Phone size={14} /> Call
              </a>
            )}
            <Link
              to={`${linkBase}${isSpanish ? "/es/contact" : "/contact"}`}
              className="flex items-center justify-center py-3 rounded-md font-semibold text-black text-sm"
              style={{ background: tenant.primary_color }}
              data-testid="mobile-sticky-estimate"
            >
              {isSpanish ? "Cotización gratis" : "Free estimate"}
            </Link>
          </div>
        )}
      </>
    );
  }

  // Light theme (default — used by non-commercial tenants)
  return (
    <nav
      className={`sticky top-0 z-30 backdrop-blur-xl transition-colors duration-300 ${scrolled ? "bg-[var(--l-bg)]/90 border-b border-[var(--l-border)]" : "bg-transparent"}`}
      data-testid="tenant-nav"
    >
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to={linkBase || "/"} className="flex items-center gap-2">
          {tenant.logo_url ? (
            <img src={tenant.logo_url} alt={tenant.name} className="h-8" />
          ) : (
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white font-cormorant font-semibold"
              style={{ background: tenant.primary_color }}
            >
              {tenant.name[0]}
            </div>
          )}
          <div className="font-cormorant text-2xl font-semibold tracking-tight" style={{ color: "var(--l-text)" }}>{tenant.name}</div>
        </Link>
        <div className="hidden md:flex items-center gap-8 font-outfit text-sm">
          {(site?.nav || []).map((n) => (
            <Link
              key={n.href}
              to={`${linkBase}${n.href === "/" ? "" : n.href}` || "/"}
              className="hover:opacity-70"
              style={{ color: "var(--l-text-sec)" }}
            >
              {n.label}
            </Link>
          ))}
        </div>
        <Link
          to={`${linkBase}/contact`}
          className="leen-btn text-sm"
          style={{ background: tenant.primary_color }}
        >
          Get started
        </Link>
      </div>
    </nav>
  );
}

function Footer({ tenant, site, linkBase, theme, isSpanish = false, enEquivalent = "/", esEquivalent = "/es", hasSpanishAlt = false, hasEnglishAlt = false }) {
  const s = site?.settings || {};
  const isDark = theme === "dark-commercial";
  const columns = site?.footer?.columns || [];
  const footerStyle = isDark ? {
    backgroundImage: "linear-gradient(180deg, rgba(13,13,13,0.9), rgba(13,13,13,0.95)), url(/granite-header-1280.jpg)",
    backgroundSize: "cover",
    backgroundPosition: "center",
  } : {};
  return (
    <footer className={`border-t border-[var(--l-border)] pt-16 pb-8 mt-16`} style={footerStyle} data-testid="tenant-footer">
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            {tenant.logo_url ? (
              <img src={tenant.logo_url} alt={tenant.name} className={isDark ? "h-12" : "h-8"} />
            ) : (
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-cab font-black" style={{ background: tenant.primary_color }}>
                {tenant.name[0]}
              </div>
            )}
            <div className={`${isDark ? "font-cab font-black uppercase" : "font-cormorant font-semibold"} text-xl`} style={{ color: "var(--l-text)" }}>{tenant.name}</div>
          </div>
          <div className={`${isDark ? "font-inter" : "font-outfit"} text-sm leading-relaxed`} style={{ color: "var(--l-text-sec)" }}>{isSpanish ? "El estándar de estacionamientos para administradores de propiedades en Texas." : tenant.tagline}</div>
          {isDark && (
            <div className="mt-6 space-y-2">
              {(s.business_hours || []).map((h) => (
                <div key={h.day} className="flex justify-between text-xs font-mono-plex" style={{ color: "var(--l-text-muted)" }}>
                  <span className="uppercase">{h.day}</span><span>{h.hours}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {columns.map((col, idx) => (
          <div key={idx}>
            <div className={`text-xs uppercase tracking-widest mb-4 ${isDark ? "font-inter font-bold" : "font-outfit"}`} style={{ color: isDark ? "var(--l-accent-yellow)" : "var(--l-text-sec)" }}>{t(col.heading, isSpanish)}</div>
            <div className={`space-y-2 text-sm ${isDark ? "font-inter" : "font-outfit"}`}>
              {(col.links || []).map((l, lidx) => (
                <Link key={`${idx}-${lidx}-${l.label}`} to={`${linkBase}${l.href === "/" ? "" : l.href}` || "/"} className="block hover:text-white transition-colors" style={{ color: "var(--l-text-sec)" }}>
                  {t(l.label, isSpanish)}
                </Link>
              ))}
            </div>
          </div>
        ))}

        <div>
          <div className={`text-xs uppercase tracking-widest mb-4 ${isDark ? "font-inter font-bold" : "font-outfit"}`} style={{ color: isDark ? "var(--l-accent-yellow)" : "var(--l-text-sec)" }}>{isSpanish ? "Contacto" : "Contact"}</div>
          <div className={`space-y-3 text-sm ${isDark ? "font-inter" : "font-outfit"}`} style={{ color: "var(--l-text)" }}>
            {(s.phones || []).map((p) => (
              <a key={p} href={`tel:${p.replace(/[^0-9+]/g, "")}`} className="flex items-center gap-2 hover:text-[var(--l-accent-yellow)] transition-colors">
                <Phone size={14} /> <span className="font-semibold">{p}</span>
              </a>
            ))}
            {(s.emails || []).map((e) => (
              <a key={e} href={`mailto:${e}`} className="flex items-center gap-2 hover:text-[var(--l-accent-yellow)] transition-colors">
                <Mail size={14} /> {e}
              </a>
            ))}
            {tenant.address && (
              <div className="flex items-start gap-2" style={{ color: "var(--l-text-sec)" }}>
                <span>📍</span><span>{tenant.address}</span>
              </div>
            )}
          </div>
          {isDark && (
            <div className="mt-6 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: "var(--l-accent-yellow)" }} />
              <span className="text-[11px] uppercase tracking-widest font-mono-plex" style={{ color: "var(--l-text-muted)" }}>{isSpanish ? "Con licencia · Con fianza · Asegurados" : "Licensed · Bonded · Insured"}</span>
            </div>
          )}
          <div className="mt-6">
            <LanguageSwitcher
              isSpanish={isSpanish}
              enEquivalent={enEquivalent}
              esEquivalent={esEquivalent}
              hasSpanishAlt={hasSpanishAlt}
              hasEnglishAlt={hasEnglishAlt}
              linkBase={linkBase}
              variant="footer"
            />
          </div>
        </div>
      </div>

      <div className={`max-w-7xl mx-auto px-6 mt-12 pt-6 border-t border-[var(--l-border)] text-xs flex flex-col md:flex-row items-center justify-between gap-3 ${isDark ? "font-mono-plex" : "font-outfit"}`} style={{ color: "var(--l-text-muted)" }}>
        <span>
          {isSpanish
            ? `© ${new Date().getFullYear()} ${tenant.name}. Empresa familiar. Con licencia y asegurada. Todos los derechos reservados.`
            : (site?.footer?.copyright || `© ${new Date().getFullYear()} ${tenant.name}. All rights reserved.`)}
        </span>

        {/* Kit attribution — appended per PlugitAI branding request */}
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em]"
             style={{ color: isDark ? "rgba(245,245,240,0.35)" : "rgba(20,20,20,0.45)" }}
             data-testid="footer-kit-attribution">
          <a
            href="https://www.plugitai.com"
            target="_blank"
            rel="noreferrer"
            className="hover:text-[var(--l-accent-yellow)] transition-colors"
            data-testid="footer-built-by-kit"
          >
            100% built by Kit
          </a>
          <span aria-hidden="true">·</span>
          <a
            href="https://www.plugitai.com/about"
            target="_blank"
            rel="noreferrer"
            className="hover:text-[var(--l-accent-yellow)] transition-colors"
            data-testid="footer-practically-built-by"
          >
            practically built by
          </a>
        </div>

        <div className="flex items-center gap-4">
          {/* Discreet "Powered by NariAI" mark — blends into the footer
              background. Visible only if a visitor actively looks for it. */}
          <Link
            to={`${linkBase}/powered-by-nariai`}
            data-tenant-link="raw"
            data-testid="powered-by-nariai-link"
            aria-label="Powered by NariAI"
            style={{
              color: isDark ? "rgba(245,245,240,0.14)" : "rgba(20,20,20,0.25)",
              fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
              fontSize: 10,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              textDecoration: "none",
              transition: "color 300ms ease, letter-spacing 300ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = isDark ? "rgba(212,175,55,0.7)" : "rgba(212,175,55,0.9)";
              e.currentTarget.style.letterSpacing = "0.18em";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = isDark ? "rgba(245,245,240,0.14)" : "rgba(20,20,20,0.25)";
              e.currentTarget.style.letterSpacing = "0.14em";
            }}
          >
            {isSpanish ? "Diseñado por NariAI" : "Powered by NariAI"}
          </Link>
          {isDark && <span className="uppercase tracking-widest">{isSpanish ? "Hecho en Texas" : "Made in Texas"}</span>}
        </div>
      </div>
    </footer>
  );
}

// Gold check-mark icon used by BuiltByNariai cards.
function NariaiGoldCheck({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <defs>
        <linearGradient id="nariai-gold-check" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#F5E6B3" />
          <stop offset="45%" stopColor="#E5C77A" />
          <stop offset="100%" stopColor="#A1782E" />
        </linearGradient>
      </defs>
      <circle cx="12" cy="12" r="10.5" stroke="url(#nariai-gold-check)" strokeWidth="1.2" fill="rgba(212,175,55,0.06)" />
      <path d="M7.5 12.4 L10.6 15.4 L16.5 8.9" stroke="url(#nariai-gold-check)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

// ---------- Built by NariAI — static architect's signature ----------
// Full-width dark section at the bottom of every tenant page. Zero external
// links, zero navigation away from the tenant site. The only interactive
// element is a `tel:` link to Chris's number — this opens the phone dialer,
// does NOT navigate away from the current site.
function BuiltByNariai({ isSpanish = false }) {
  const [visible, setVisible] = useState(false);
  const [caseOpen, setCaseOpen] = useState(false);
  const rootRef = useRef(null);
  const caseRef = useRef(null);
  useEffect(() => {
    if (!rootRef.current) return;
    const el = rootRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setVisible(true); });
      },
      { threshold: 0.12, rootMargin: "0px 0px -80px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const copy = isSpanish
    ? {
        eyebrow: "FIRMA DEL ARQUITECTO",
        headline: "Diseñado por NariAI",
        subhead: "Somos el equipo que imaginó, diseñó y desarrolló este sitio web.",
        body1: "Este sitio web no se construyó a partir de una plantilla.",
        body2: "Fue diseñado para generar más clientes potenciales, responder más rápido, mejorar el posicionamiento en Google y hacer crecer este negocio con el tiempo.",
        body3: "Cada elemento fue creado con un solo objetivo:",
        body4: "Ayudar a los negocios a ganar más clientes.",
        cards: [
          { title: "Diseño Web Personalizado", body: "Sitios web profesionales creados para convertir visitantes en clientes." },
          { title: "Automatización con IA", body: "Herramientas inteligentes que ahorran tiempo y nunca pierden un cliente potencial." },
          { title: "Optimización SEO", body: "Diseñado para posicionar mejor y generar tráfico a largo plazo." },
          { title: "Mantenimiento Continuo", body: "Seguridad, actualizaciones, respaldos, monitoreo y mejoras constantes." },
        ],
        ctaLead: "¿Le interesa construir algo así para su negocio?",
        ctaCall: "Llame a Chris directamente.",
        noText: "Sin formularios.  Sin ventanas emergentes.  Sin redirecciones.",
        signature: "NariAI — Diseño, ingeniería y crecimiento a largo plazo.",
        caseToggleClosed: "Ver el estudio de este sitio",
        caseToggleOpen: "Cerrar el estudio",
        caseTitle: "Este sitio, en cifras.",
        caseSubtitle: "Un vistazo transparente a lo que se construyó para este negocio.",
        caseMetrics: [
          { value: "266+", label: "Páginas de contenido local indexadas" },
          { value: "20", label: "Páginas nativas en español (hreflang completo)" },
          { value: "8", label: "Herramientas interactivas para clientes" },
          { value: "6", label: "Bloques Schema.org por página" },
          { value: "<200 ms", label: "Tiempo de respuesta del servidor" },
          { value: "100 %", label: "Móvil-primero · Núcleo Web Vitals en verde" },
        ],
        caseFooter: "Sin ventas. Sin seguimiento entre sitios. Este sitio le pertenece a este negocio.",
      }
    : {
        eyebrow: "ARCHITECT'S SIGNATURE",
        headline: "Built by NariAI",
        subhead: "We're the team that envisioned, designed, and engineered this website.",
        body1: "This website wasn't built from a template.",
        body2: "It was designed to help generate more leads, answer customers faster, improve Google rankings, and grow this business over time.",
        body3: "Every feature you see was created with one goal:",
        body4: "Helping businesses win more customers.",
        cards: [
          { title: "Custom Website Design", body: "Professional websites built to convert visitors into customers." },
          { title: "AI Business Automation", body: "Smart tools that save time and never miss a lead." },
          { title: "Search Engine Optimization", body: "Built to rank higher and generate long-term traffic." },
          { title: "Ongoing Website Care", body: "Security, updates, backups, monitoring, and continuous improvements." },
        ],
        ctaLead: "Interested in building something like this for your business?",
        ctaCall: "Call Chris directly.",
        noText: "No forms.  No popups.  No redirects.",
        signature: "NariAI — Design, engineering, and long-term growth.",
        caseToggleClosed: "Read this site's case study",
        caseToggleOpen: "Close case study",
        caseTitle: "This site, by the numbers.",
        caseSubtitle: "A transparent look at what was engineered for this business.",
        caseMetrics: [
          { value: "266+", label: "Pages of indexed, local SEO content" },
          { value: "20", label: "Native Spanish pages (full hreflang)" },
          { value: "8", label: "Interactive customer-facing tools" },
          { value: "6", label: "Schema.org JSON-LD blocks per page" },
          { value: "<200 ms", label: "Median server response time" },
          { value: "100 %", label: "Mobile-first · Core Web Vitals green" },
        ],
        caseFooter: "No selling. No cross-site tracking. This site belongs to this business.",
      };

  const GOLD = "#d4af37";
  const BG = "#0b0b0b";

  const cardBaseStyle = {
    background: "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0) 100%)",
    border: "1px solid rgba(212,175,55,0.18)",
    borderRadius: 4,
    padding: "28px 24px",
    transition: "transform 500ms cubic-bezier(.2,.8,.2,1), border-color 400ms ease, background 400ms ease",
    position: "relative",
    overflow: "hidden",
  };

  return (
    <section
      ref={rootRef}
      data-testid="built-by-nariai"
      aria-labelledby="built-by-nariai-headline"
      style={{
        background: BG,
        color: "#F5F5F0",
        position: "relative",
        overflow: "hidden",
        borderTop: "1px solid rgba(212,175,55,0.18)",
      }}
    >
      {/* subtle gold top-hairline glow */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 1,
          background: "linear-gradient(90deg, transparent 0%, rgba(212,175,55,0.55) 50%, transparent 100%)",
          filter: "blur(0.6px)",
        }}
      />
      {/* faint background gold radial */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: "-20%",
          left: "50%",
          transform: "translateX(-50%)",
          width: 900,
          height: 900,
          background: "radial-gradient(closest-side, rgba(212,175,55,0.08), transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <div
        className="max-w-6xl mx-auto"
        style={{
          padding: "88px 24px 64px 24px",
          position: "relative",
          transform: visible ? "translateY(0)" : "translateY(24px)",
          opacity: visible ? 1 : 0,
          transition: "transform 800ms cubic-bezier(.2,.8,.2,1), opacity 800ms ease",
        }}
      >
        {/* Header block */}
        <div className="text-center max-w-3xl mx-auto">
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
              fontSize: 11,
              letterSpacing: "0.28em",
              color: GOLD,
              textTransform: "uppercase",
            }}
            data-testid="built-by-nariai-eyebrow"
          >
            <span aria-hidden style={{ width: 28, height: 1, background: GOLD, opacity: 0.6 }} />
            {copy.eyebrow}
            <span aria-hidden style={{ width: 28, height: 1, background: GOLD, opacity: 0.6 }} />
          </div>
          <h2
            id="built-by-nariai-headline"
            className="font-cab"
            style={{
              fontSize: "clamp(2.25rem, 4.4vw, 3.75rem)",
              lineHeight: 1.05,
              letterSpacing: "-0.01em",
              marginTop: 22,
              color: "#FFFFFF",
              fontWeight: 800,
            }}
            data-testid="built-by-nariai-headline"
          >
            {copy.headline.split("NariAI")[0]}
            <span style={{ color: GOLD }}>NariAI</span>
            {copy.headline.split("NariAI")[1] || ""}
          </h2>
          <p
            style={{
              marginTop: 20,
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: "1.125rem",
              lineHeight: 1.6,
              color: "rgba(245,245,240,0.82)",
              maxWidth: 640,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            {copy.subhead}
          </p>
          <div
            style={{
              marginTop: 26,
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: "0.95rem",
              lineHeight: 1.75,
              color: "rgba(245,245,240,0.68)",
              maxWidth: 640,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            <p style={{ margin: 0 }}>{copy.body1}</p>
            <p style={{ margin: "10px 0 0 0" }}>{copy.body2}</p>
            <p style={{ margin: "18px 0 0 0", color: "rgba(245,245,240,0.82)" }}>{copy.body3}</p>
            <p style={{ margin: "6px 0 0 0", color: "#FFFFFF", fontWeight: 600, letterSpacing: "0.01em" }}>{copy.body4}</p>
          </div>
        </div>

        {/* 4 icon cards */}
        <div
          className="grid gap-4 sm:gap-5 mt-14"
          style={{
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          }}
        >
          {copy.cards.map((c, i) => (
            <div
              key={i}
              data-testid={`built-by-nariai-card-${i + 1}`}
              className="built-by-nariai-card"
              style={{
                ...cardBaseStyle,
                transform: visible ? "translateY(0)" : "translateY(16px)",
                opacity: visible ? 1 : 0,
                transitionDelay: `${180 + i * 80}ms`,
              }}
            >
              <div style={{ marginBottom: 14 }}>
                <NariaiGoldCheck />
              </div>
              <div
                className="font-cab"
                style={{
                  fontSize: "1.05rem",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  letterSpacing: "-0.005em",
                  marginBottom: 8,
                }}
              >
                {c.title}
              </div>
              <div
                style={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: "0.875rem",
                  lineHeight: 1.55,
                  color: "rgba(245,245,240,0.7)",
                }}
              >
                {c.body}
              </div>
              {/* subtle bottom-gold underline on hover */}
              <span
                aria-hidden
                className="built-by-nariai-card-underline"
                style={{
                  position: "absolute",
                  left: 24,
                  right: 24,
                  bottom: 14,
                  height: 1,
                  background: "linear-gradient(90deg, rgba(212,175,55,0.6), transparent)",
                  opacity: 0,
                  transition: "opacity 400ms ease",
                }}
              />
            </div>
          ))}
        </div>

        {/* Bottom CTA — phone only, no forms/links */}
        <div
          className="mt-14 text-center"
          style={{
            padding: "36px 24px 8px 24px",
            borderTop: "1px solid rgba(212,175,55,0.16)",
            transform: visible ? "translateY(0)" : "translateY(12px)",
            opacity: visible ? 1 : 0,
            transition: "transform 800ms cubic-bezier(.2,.8,.2,1) 500ms, opacity 800ms ease 500ms",
          }}
        >
          <p
            style={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: "1rem",
              color: "rgba(245,245,240,0.82)",
              margin: 0,
            }}
          >
            {copy.ctaLead}
          </p>
          <p
            style={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: "1rem",
              color: "rgba(245,245,240,0.72)",
              margin: "6px 0 22px 0",
            }}
          >
            {copy.ctaCall}
          </p>
          <a
            href="tel:+12148155966"
            data-testid="built-by-nariai-phone"
            data-tenant-link="raw"
            aria-label="Call Chris at NariAI at (214) 815-5966"
            style={{
              display: "inline-block",
              fontFamily: "'Cabinet Grotesk', 'Inter', system-ui, sans-serif",
              fontSize: "clamp(2rem, 4.4vw, 3.25rem)",
              fontWeight: 900,
              letterSpacing: "-0.01em",
              color: GOLD,
              textDecoration: "none",
              textShadow: "0 4px 22px rgba(212,175,55,0.28)",
              transition: "transform 220ms ease, color 220ms ease, text-shadow 220ms ease",
              fontVariantNumeric: "tabular-nums",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.color = "#F0D67A";
              e.currentTarget.style.textShadow = "0 6px 26px rgba(212,175,55,0.45)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.color = GOLD;
              e.currentTarget.style.textShadow = "0 4px 22px rgba(212,175,55,0.28)";
            }}
          >
            214-815-5966
          </a>
          <p
            style={{
              marginTop: 22,
              fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
              fontSize: 11,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "rgba(245,245,240,0.5)",
            }}
          >
            {copy.noText}
          </p>
        </div>

        {/* Signature line */}
        <div
          style={{
            marginTop: 40,
            textAlign: "center",
            fontFamily: "'Cormorant Garamond', 'Cormorant', serif",
            fontStyle: "italic",
            fontSize: "0.95rem",
            color: "rgba(245,245,240,0.42)",
            letterSpacing: "0.02em",
          }}
        >
          {copy.signature}
        </div>

        {/* Case-study inline expander — NOT a popup. Slides open inside this
            same section. No external navigation. */}
        <div style={{ marginTop: 22, textAlign: "center" }}>
          <button
            type="button"
            onClick={() => {
              const next = !caseOpen;
              setCaseOpen(next);
              // When opening, gently scroll the expander into view a moment
              // after the animation begins.
              if (next) {
                setTimeout(() => {
                  if (caseRef.current) {
                    caseRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
                  }
                }, 220);
              }
            }}
            aria-expanded={caseOpen}
            aria-controls="built-by-nariai-case"
            data-testid="built-by-nariai-case-toggle"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 4px",
              color: "rgba(212,175,55,0.9)",
              fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              transition: "color 200ms ease, letter-spacing 200ms ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "#F0D67A"; e.currentTarget.style.letterSpacing = "0.22em"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(212,175,55,0.9)"; e.currentTarget.style.letterSpacing = "0.18em"; }}
          >
            <span
              aria-hidden
              style={{
                display: "inline-block",
                transform: caseOpen ? "rotate(90deg)" : "rotate(0deg)",
                transition: "transform 280ms cubic-bezier(.2,.8,.2,1)",
                fontSize: 10,
              }}
            >
              ▸
            </span>
            {caseOpen ? copy.caseToggleOpen : copy.caseToggleClosed}
          </button>
        </div>

        {/* Expander body */}
        <div
          id="built-by-nariai-case"
          ref={caseRef}
          data-testid="built-by-nariai-case-panel"
          aria-hidden={!caseOpen}
          style={{
            overflow: "hidden",
            maxHeight: caseOpen ? 800 : 0,
            opacity: caseOpen ? 1 : 0,
            transition: "max-height 520ms cubic-bezier(.2,.8,.2,1), opacity 400ms ease",
            marginTop: caseOpen ? 24 : 0,
          }}
        >
          <div
            style={{
              maxWidth: 880,
              marginLeft: "auto",
              marginRight: "auto",
              padding: "28px 24px 8px 24px",
              borderTop: "1px solid rgba(212,175,55,0.16)",
              borderBottom: "1px solid rgba(212,175,55,0.10)",
              background: "linear-gradient(180deg, rgba(212,175,55,0.03) 0%, rgba(0,0,0,0) 100%)",
            }}
          >
            <div style={{ textAlign: "center", marginBottom: 26 }}>
              <div
                className="font-cab"
                style={{
                  fontSize: "clamp(1.4rem, 2.6vw, 1.9rem)",
                  color: "#FFFFFF",
                  fontWeight: 800,
                  letterSpacing: "-0.005em",
                }}
              >
                {copy.caseTitle}
              </div>
              <div
                style={{
                  marginTop: 8,
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: "0.9rem",
                  color: "rgba(245,245,240,0.65)",
                }}
              >
                {copy.caseSubtitle}
              </div>
            </div>

            <div
              className="grid gap-4"
              style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}
            >
              {copy.caseMetrics.map((m, i) => (
                <div
                  key={i}
                  data-testid={`built-by-nariai-metric-${i + 1}`}
                  style={{
                    textAlign: "center",
                    padding: "18px 12px",
                    borderRadius: 2,
                    background: "rgba(255,255,255,0.02)",
                    border: "1px solid rgba(212,175,55,0.12)",
                    transform: caseOpen ? "translateY(0)" : "translateY(8px)",
                    opacity: caseOpen ? 1 : 0,
                    transition: `transform 500ms cubic-bezier(.2,.8,.2,1) ${120 + i * 60}ms, opacity 500ms ease ${120 + i * 60}ms`,
                  }}
                >
                  <div
                    className="font-cab"
                    style={{
                      fontSize: "clamp(1.5rem, 3vw, 2rem)",
                      color: GOLD,
                      fontWeight: 900,
                      letterSpacing: "-0.01em",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {m.value}
                  </div>
                  <div
                    style={{
                      marginTop: 6,
                      fontFamily: "'Inter', system-ui, sans-serif",
                      fontSize: "0.78rem",
                      lineHeight: 1.45,
                      color: "rgba(245,245,240,0.7)",
                    }}
                  >
                    {m.label}
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                marginTop: 22,
                paddingTop: 16,
                borderTop: "1px dashed rgba(212,175,55,0.18)",
                textAlign: "center",
                fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                fontSize: 10,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "rgba(245,245,240,0.45)",
              }}
            >
              {copy.caseFooter}
            </div>
          </div>
        </div>
      </div>

      {/* Local CSS — card hover underline reveal */}
      <style>{`
        .built-by-nariai-card:hover {
          transform: translateY(-3px);
          border-color: rgba(212,175,55,0.4) !important;
          background: linear-gradient(180deg, rgba(212,175,55,0.05) 0%, rgba(255,255,255,0) 100%) !important;
        }
        .built-by-nariai-card:hover .built-by-nariai-card-underline {
          opacity: 1;
        }
      `}</style>
    </section>
  );
}

// ---------- Language Switcher ----------
// EN | ES pill toggle. On EN page: EN active, ES links to Spanish equivalent
// (or /es home if no direct alternate). On ES page: ES active, EN links back.
function LanguageSwitcher({ isSpanish, enEquivalent, esEquivalent, hasSpanishAlt, hasEnglishAlt, linkBase, variant = "nav-dark", onNavigate }) {
  // Compute effective targets. If the alternate translation for the current
  // page doesn't exist, fall back to the homepage of that language.
  const esTarget = `${linkBase}${hasSpanishAlt ? esEquivalent : "/es"}`;
  const enTarget = `${linkBase}${hasEnglishAlt ? (enEquivalent === "/" ? "" : enEquivalent) : ""}` || "/";

  const isDark = variant.startsWith("nav-dark");
  const isCompact = variant === "nav-dark-compact";
  const wrapperStyle = {
    display: "inline-flex",
    alignItems: "center",
    padding: isCompact ? 1 : 2,
    gap: isCompact ? 1 : 2,
    borderRadius: 999,
    background: isDark ? "rgba(255,255,255,0.06)" : "rgba(20,20,20,0.4)",
    border: "1px solid rgba(229,199,122,0.35)",
    flexShrink: 0,
  };
  const pillBase = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: isCompact ? 28 : 34,
    height: isCompact ? 22 : 26,
    padding: isCompact ? "0 7px" : "0 10px",
    borderRadius: 999,
    fontSize: isCompact ? 10 : 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textDecoration: "none",
    transition: "all 200ms ease",
  };
  const activePill = {
    ...pillBase,
    background: "linear-gradient(180deg,#F0D580 0%,#C9A24B 50%,#8F6A2C 100%)",
    color: "#0D0D0D",
    boxShadow: "0 2px 8px rgba(201,162,75,0.35), inset 0 1px 0 rgba(255,255,255,0.4)",
  };
  const inactivePill = {
    ...pillBase,
    color: isDark ? "rgba(255,255,255,0.75)" : "rgba(255,255,255,0.7)",
    background: "transparent",
  };

  return (
    <div style={wrapperStyle} data-testid={`lang-switcher-${variant}`} aria-label="Language selector">
      <Link
        to={enTarget}
        onClick={onNavigate}
        style={isSpanish ? inactivePill : activePill}
        aria-current={isSpanish ? undefined : "true"}
        data-testid="lang-switch-en"
        data-tenant-link="raw"
      >
        EN
      </Link>
      <Link
        to={esTarget}
        onClick={onNavigate}
        style={isSpanish ? activePill : inactivePill}
        aria-current={isSpanish ? "true" : undefined}
        data-testid="lang-switch-es"
        data-tenant-link="raw"
      >
        ES
      </Link>
    </div>
  );
}

// ---------- AI Chat widget ----------
function ChatWidget({ tenant }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => {
    const key = `nariai_chat_session_${tenant.slug}`;
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const sid = `widget-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;
    localStorage.setItem(key, sid);
    return sid;
  });
  const scrollRef = useRef(null);
  const lastSeenRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const params = new URLSearchParams({ tenant_slug: tenant.slug, session_id: sessionId });
        if (lastSeenRef.current) params.set("since", lastSeenRef.current);
        const { data } = await axios.get(`${API}/public/chat/poll?${params.toString()}`);
        if (cancelled) return;
        const newOnes = (data.messages || []).filter((m) => !lastSeenRef.current || m.created_at > lastSeenRef.current);
        if (newOnes.length) {
          setMessages((prev) => {
            const ids = new Set(prev.map((p) => p.id).filter(Boolean));
            const merged = [...prev];
            for (const m of newOnes) {
              if (m.id && ids.has(m.id)) continue;
              merged.push({ id: m.id, role: m.sender_role === "ai" ? "assistant" : m.sender_role, sender_name: m.sender_name, content: m.body, created_at: m.created_at });
            }
            return merged;
          });
          lastSeenRef.current = newOnes[newOnes.length - 1].created_at;
        }
      } catch { /* poll error swallowed */ }
    };
    const t = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [tenant.slug, sessionId]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || busy) return;
    const msg = input.trim();
    setMessages((m) => [...m, { role: "user", content: msg, created_at: new Date().toISOString() }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/chat`, { tenant_slug: tenant.slug, session_id: sessionId, message: msg });
      setMessages((m) => [...m, { role: "assistant", sender_name: "AI", content: data.reply, created_at: new Date().toISOString() }]);
      lastSeenRef.current = new Date().toISOString();
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I'm having trouble right now." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-24 right-6 z-40 rounded-full p-4 shadow-xl"
        style={{ background: tenant.primary_color, color: "white" }}
        data-testid="chat-toggle"
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
      </button>
      {open && (
        <div className="fixed bottom-44 right-6 z-40 w-[360px] max-w-[calc(100vw-2rem)] bg-white border border-[var(--l-border)] rounded-lg shadow-2xl flex flex-col overflow-hidden" data-testid="chat-widget">
          <div className="p-4" style={{ background: tenant.primary_color, color: "white" }}>
            <div className="font-cormorant text-xl font-semibold">{tenant.name} Care</div>
            <div className="font-outfit text-xs opacity-90">Ask anything. We&apos;re here to help.</div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-auto p-4 space-y-3 max-h-[400px] font-outfit text-sm">
            {messages.length === 0 && <div style={{ color: "var(--l-text-sec)" }}>Hi! Ask me anything about our services.</div>}
            {messages.map((m, i) => {
              const isUser = m.role === "user";
              const isAdmin = m.role === "admin";
              return (
                <div key={m.id || i} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                  <div className="max-w-[80%] flex flex-col gap-1">
                    {!isUser && (
                      <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--l-text-sec)" }}>
                        {isAdmin ? (m.sender_name || `${tenant.name} team`) : "AI Assistant"}
                      </div>
                    )}
                    <div className={`px-3 py-2 rounded-lg whitespace-pre-wrap ${isUser ? "text-white" : isAdmin ? "bg-white border border-[var(--l-border)]" : "bg-[var(--l-surface)]"}`} style={isUser ? { background: tenant.primary_color } : { color: "var(--l-text)" }}>
                      {m.content}
                    </div>
                  </div>
                </div>
              );
            })}
            {busy && <div className="text-xs" style={{ color: "var(--l-text-sec)" }}>typing…</div>}
          </div>
          <form onSubmit={send} className="border-t border-[var(--l-border)] p-3 flex gap-2">
            <input className="leen-input flex-1" placeholder="Type a message…" value={input} onChange={(e) => setInput(e.target.value)} data-testid="chat-input" />
            <button type="submit" className="px-4 rounded-md text-white" style={{ background: tenant.primary_color }} disabled={busy} data-testid="chat-send">
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
